import Component from '@glimmer/component';
import { on } from '@ember/modifier';
import { htmlSafe } from '@ember/template';
import { eq } from 'tio-ui/utilities';
import { PhCheckCircle, PhXCircle, PhWarning, PhInfo, PhX } from 'ember-phosphor-icons';
import { getToastTokens } from '../color-tokens.ts';
import type { ToastIntent } from '../color-tokens.ts';

// ---------------------------------------------------------------------------
// Toast item interface — shared between service and component
// ---------------------------------------------------------------------------

export interface ToastItem {
  id: string;
  message: string;
  intent: ToastIntent;
  duration: number;
  visible: boolean;
  /** Optional action label (renders a clickable button on the toast) */
  actionLabel?: string;
  /** Callback when action is clicked */
  onAction?: () => void;
}

// ---------------------------------------------------------------------------
// Individual Toast component
// ---------------------------------------------------------------------------

interface SingleToastSignature {
  Args: {
    toast: ToastItem;
    onDismiss: (id: string) => void;
  };
  Element: HTMLDivElement;
}

class SingleToast extends Component<SingleToastSignature> {
  get tokens() {
    return getToastTokens(this.args.toast.intent);
  }

  get roleAttr() {
    return this.args.toast.intent === 'error' ? 'alert' : 'status';
  }

  get ariaLive() {
    return this.args.toast.intent === 'error' ? 'assertive' : 'polite';
  }

  get showProgress() {
    return this.args.toast.duration > 0;
  }

  get progressStyle() {
    return htmlSafe(`animation-duration: ${this.args.toast.duration}ms`);
  }

  handleDismiss = () => {
    this.args.onDismiss(this.args.toast.id);
  };

  handleAction = () => {
    this.args.toast.onAction?.();
    this.args.onDismiss(this.args.toast.id);
  };

  <template>
    <div
      data-test-toast
      data-test-toast-intent={{@toast.intent}}
      role={{this.roleAttr}}
      aria-live={{this.ariaLive}}
      class="relative overflow-hidden w-80 border-l-4 rounded-xl shadow-lg ring-1 backdrop-blur-md transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)]
        {{this.tokens.bg}}
        {{this.tokens.border}}
        {{this.tokens.ring}}
        {{if
          @toast.visible
          'opacity-100 translate-x-0 scale-100'
          'opacity-0 translate-x-8 scale-95'
        }}"
      ...attributes
    >
      <div class="flex items-start gap-3 p-4">
        {{! Intent icon }}
        <div class="flex-shrink-0 mt-0.5" data-test-toast-icon>
          {{#if (eq @toast.intent "success")}}
            <PhCheckCircle @size={{20}} class={{this.tokens.icon}} />
          {{else if (eq @toast.intent "error")}}
            <PhXCircle @size={{20}} class={{this.tokens.icon}} />
          {{else if (eq @toast.intent "warning")}}
            <PhWarning @size={{20}} class={{this.tokens.icon}} />
          {{else}}
            <PhInfo @size={{20}} class={{this.tokens.icon}} />
          {{/if}}
        </div>

        {{! Message }}
        <div class="flex-1 min-w-0">
          <p data-test-toast-message class="text-sm font-medium text-gray-900 leading-snug">
            {{@toast.message}}
          </p>
          {{#if @toast.actionLabel}}
            <button
              type="button"
              class="mt-1.5 text-xs font-semibold text-[#006c9a] hover:text-[#005a82] dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
              data-test-toast-action
              {{on "click" this.handleAction}}
            >
              {{@toast.actionLabel}}
            </button>
          {{/if}}
        </div>

        {{! Close button }}
        <button
          type="button"
          class="flex-shrink-0 p-1 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 transition-colors"
          data-test-toast-close
          aria-label="Dismiss"
          {{on "click" this.handleDismiss}}
        >
          <PhX @size={{14}} />
        </button>
      </div>

      {{! Success confetti burst }}
      {{#if (eq @toast.intent "success")}}
        <div
          class="absolute top-2 left-6 pointer-events-none
            {{if @toast.visible 'reimagined-confetti' ''}}"
        >
          <span class="reimagined-confetti-dot reimagined-confetti-1 bg-emerald-400"></span>
          <span class="reimagined-confetti-dot reimagined-confetti-2 bg-teal-400"></span>
          <span class="reimagined-confetti-dot reimagined-confetti-3 bg-green-400"></span>
          <span class="reimagined-confetti-dot reimagined-confetti-4 bg-emerald-300"></span>
          <span class="reimagined-confetti-dot reimagined-confetti-5 bg-cyan-400"></span>
        </div>
      {{/if}}

      {{! Auto-dismiss progress bar }}
      {{#if this.showProgress}}
        <div class="h-0.5 w-full bg-gray-100">
          <div
            data-test-toast-progress
            class="h-full
              {{this.tokens.progress}}
              origin-left
              {{if @toast.visible 'reimagined-toast-progress' ''}}"
            style={{this.progressStyle}}
          ></div>
        </div>
      {{/if}}
    </div>
  </template>
}

// ---------------------------------------------------------------------------
// ToastContainer – renders all active toasts, placed once in layout
// ---------------------------------------------------------------------------

interface ToastContainerSignature {
  Args: {
    /** Array of toast items to render (from the toast service) */
    toasts: ToastItem[];
    /** Called when a toast is dismissed */
    onDismiss: (id: string) => void;
  };
  Element: HTMLDivElement;
}

/**
 * ToastContainer – renders a stack of toast notifications.
 *
 * Place once in your app layout template. Bind to the toast service's
 * `toasts` array and `dismiss` method.
 *
 * @example
 * ```gts
 * import ToastContainer from 'tio-reimagined/components/toast';
 *
 * <ToastContainer @toasts={{this.toastService.toasts}} @onDismiss={{this.toastService.dismiss}} />
 * ```
 */
export default class ToastContainer extends Component<ToastContainerSignature> {
  <template>
    <div
      data-test-toast-container
      class="fixed bottom-6 right-6 z-[60] flex flex-col-reverse gap-3 pointer-events-none"
      aria-label="Notifications"
      ...attributes
    >
      {{#each @toasts as |toast|}}
        <div class="pointer-events-auto">
          <SingleToast @toast={{toast}} @onDismiss={{@onDismiss}} />
        </div>
      {{/each}}
    </div>
  </template>
}

export type { ToastContainerSignature, ToastIntent };
